import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdQuiz, MdLightbulb, MdLink, MdEdit, MdDelete, MdFileCopy, MdVisibility, MdFilterList, MdSearch, MdClose, MdSave, MdAdd, MdRemove } from "react-icons/md";
import { LearningCard } from "../index";
import { getCachedAvailableLatteImages } from "utils/imageUtils";
import ConfirmPopup from "components/popup/ConfirmPopup";

interface CardManagerProps {
  cards: LearningCard[];
  onCardSelect: (card: LearningCard) => void;
  onCardDelete: (cardId: string) => void;
  onCardDuplicate: (card: LearningCard) => void;
  onCardStatusToggle: (cardId: string, newStatus: "draft" | "published") => void;
  onCardUpdate: (cardId: string, updatedCard: Partial<LearningCard>) => void;
  onAddDay: (navigateToDay: (day: number) => void) => void;
  onRemoveDay: (dayNumber: number) => void;
}

const CardManager = ({ cards, onCardSelect, onCardDelete, onCardDuplicate, onCardStatusToggle, onCardUpdate, onAddDay, onRemoveDay }: CardManagerProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string>("All");
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [currentDay, setCurrentDay] = useState(1);
  const [showAllModules, setShowAllModules] = useState(false);
  const cardsPerDay = 12;
  
  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<LearningCard | null>(null);
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  
  // Quiz specific state
  const [quizData, setQuizData] = useState({
    question: "",
    optionA: "",
    optionB: "",
    correctAnswers: { a: false, b: false }
  });

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    message: '',
    title: 'Confirm',
    onConfirm: () => {},
  });

  // Dynamic image loading state
  const [availableImages, setAvailableImages] = useState<Array<{id: number, url: string, name: string}>>([]);
  
  // Load available images dynamically
  useEffect(() => {
    const loadAvailableImages = async () => {
      try {
        const imageNumbers = await getCachedAvailableLatteImages();
        const images = imageNumbers.map((num) => ({
          id: num,
          url: `/assets/img/latte/${num}.png`,
          name: `Image ${num}`
        }));
        setAvailableImages(images);
      } catch (error) {
        console.error('Failed to load available images in CardManager:', error);
        // Fallback to basic images
        const fallbackImages = [1, 2, 3, 4, 5, 6].map((num) => ({
          id: num,
          url: `/assets/img/latte/${num}.png`,
          name: `Image ${num}`
        }));
        setAvailableImages(fallbackImages);
      }
    };
    
    loadAvailableImages();
  }, []);

  const getCardIcon = (type: LearningCard["type"]) => {
    switch (type) {
      case "Quiz":
        return <MdQuiz className="h-4 w-4 text-green-600" />;
      case "Tip":
        return <MdLightbulb className="h-4 w-4 text-yellow-600" />;
      case "Link":
        return <MdLink className="h-4 w-4 text-blue-600" />;
    }
  };

  const getStatusBadge = (status: LearningCard["status"]) => {
    return status === "published" 
      ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
      : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300";
  };


  // Filter cards based on search and filters
  const filteredCards = cards.filter(card => {
    const matchesSearch = card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         card.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === "All" || card.type === selectedType;
    const matchesStatus = selectedStatus === "All" || card.status === selectedStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const cardTypes = ["All", "Quiz", "Tip", "Link"];
  const statusOptions = ["All", "draft", "published"];

  const handleDeleteCard = (cardId: string, cardTitle: string) => {
    setConfirmConfig({
      isOpen: true,
      message: `Are you sure you want to delete "${cardTitle}"? This action cannot be undone.`,
      title: 'Delete Card',
      onConfirm: () => onCardDelete(cardId),
    });
  };

  const handleRemoveDay = (dayNumber: number) => {
    setConfirmConfig({
      isOpen: true,
      message: `Are you sure you want to remove all cards from Day ${dayNumber}? This action cannot be undone.`,
      title: 'Remove Day',
      onConfirm: () => onRemoveDay(dayNumber),
    });
  };

  // Day-based grouping logic using card IDs
  const cardsByDay = filteredCards.reduce((acc, card) => {
    const dayMatch = card.id.match(/^day(\d+)_/);
    const dayNumber = dayMatch ? parseInt(dayMatch[1]) : 1;
    
    if (!acc[dayNumber]) {
      acc[dayNumber] = [];
    }
    acc[dayNumber].push(card);
    
    return acc;
  }, {} as Record<number, typeof filteredCards>);
  
  const availableDays = Object.keys(cardsByDay).map(d => parseInt(d)).sort((a, b) => a - b);
  const totalDays = availableDays.length;
  const actualCurrentDay = availableDays.includes(currentDay) ? currentDay : (availableDays[0] || 1);
  const currentCards = showAllModules ? filteredCards : (cardsByDay[actualCurrentDay] || []);

  // Reset to day 1 when filters change
  React.useEffect(() => {
    setCurrentDay(1);
    setShowAllModules(false);
  }, [searchQuery, selectedType, selectedStatus]);

  const handleDayChange = (day: number) => {
    setCurrentDay(day);
    setShowAllModules(false);
  };

  const handleToggleShowAllModules = () => {
    if (showAllModules) {
      // If currently showing all modules, go back to day view
      setShowAllModules(false);
      setCurrentDay(1);
    } else {
      // If in day view, show all modules
      setShowAllModules(true);
      setSearchQuery("");
      setSelectedType("All");
      setSelectedStatus("All");
    }
  };

  const parseQuizContent = (content: string) => {
    const lines = content.split('\n');
    const question = lines[0] || "";
    const optionA = lines[2]?.replace('a) ', '') || "";
    const optionB = lines[3]?.replace('b) ', '') || "";
    const answerLine = lines.find(line => line.startsWith('Answer:'));
    
    // Parse multiple correct answers
    const correctAnswers = { a: false, b: false };
    if (answerLine) {
      correctAnswers.a = answerLine.includes('a');
      correctAnswers.b = answerLine.includes('b');
    }
    
    return { question, optionA, optionB, correctAnswers };
  };

  const formatQuizContent = (quiz: typeof quizData) => {
    const correctAnswersList = [];
    if (quiz.correctAnswers.a) correctAnswersList.push('a');
    if (quiz.correctAnswers.b) correctAnswersList.push('b');
    const answerString = correctAnswersList.length > 0 ? correctAnswersList.join(', ') : 'none';
    
    return `${quiz.question}\n\na) ${quiz.optionA}\nb) ${quiz.optionB}\n\nAnswer: ${answerString}`;
  };

  const handleEditCard = (card: LearningCard) => {
    setEditingCard({ ...card });
    
    if (card.type === "Quiz") {
      const parsed = parseQuizContent(card.content);
      setQuizData(parsed);
    }
    
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingCard(null);
  };

  const handleSaveCard = () => {
    if (editingCard && editingCard.id) {
      let updatedCard = { ...editingCard };
      
      // If it's a quiz, format the content from quiz data
      if (editingCard.type === "Quiz") {
        updatedCard.content = formatQuizContent(quizData);
      }
      
      onCardUpdate(editingCard.id, {
        ...updatedCard,
        updatedAt: new Date().toISOString()
      });
      handleCloseModal();
    }
  };

  const handleInputChange = (field: keyof LearningCard, value: any) => {
    if (editingCard) {
      setEditingCard({ ...editingCard, [field]: value });
    }
  };

  const handleImageSelect = (imageUrl: string, imageName: string) => {
    if (editingCard && editingCard.assets && editingCard.assets.length > 0) {
      const updatedAssets = [...editingCard.assets];
      updatedAssets[0] = {
        ...updatedAssets[0],
        url: imageUrl,
        name: imageName
      };
      setEditingCard({ ...editingCard, assets: updatedAssets });
    } else if (editingCard) {
      // Create new asset if none exists
      const newAsset = {
        id: `asset_${Date.now()}_img`,
        type: "image" as const,
        name: imageName,
        url: imageUrl
      };
      setEditingCard({ ...editingCard, assets: [newAsset] });
    }
    setIsImageSelectorOpen(false);
  };

  if (cards.length === 0) {
    return (
      <Card extra="w-full p-6">
        <div className="text-center py-8">
          <div className="mx-auto h-32 w-32 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4 overflow-hidden">
            <img 
              src="/assets/img/latte/Latte-White.png" 
              alt="No modules yet" 
              className="w-full h-full object-cover"
            />
          </div>
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
            No modules created yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Create your first learning module to get started
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="w-full p-6">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          {showAllModules 
            ? `All Learning Modules (${currentCards.length} cards total)`
            : `Learning Modules - Day ${actualCurrentDay} (${currentCards.length} cards, ${filteredCards.length} total)`
          }
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {showAllModules
            ? "Viewing all learning modules across all days"
            : `Manage your created learning modules by day (${cardsPerDay} modules per day)`
          }
        </p>
      </div>

      {/* Filters and Search */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search Bar */}
          <div className="relative flex-1 min-w-64">
            <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search modules by title or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
            />
          </div>

          {/* Show All Modules Toggle Button */}
          <button
            onClick={handleToggleShowAllModules}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showAllModules
                ? "bg-brand-500 text-white hover:bg-brand-600"
                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-navy-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-700"
            }`}
          >
            {showAllModules ? "Back to Day View" : "Show All Modules"}
          </button>

          {/* Filter Dropdowns */}
          <div className="flex items-center gap-2">
            <MdFilterList className="h-4 w-4 text-gray-500" />
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
            >
              {cardTypes.map(type => (
                <option key={type} value={type}>{type === "All" ? "All Modules" : `${type} Modules`}</option>
              ))}
            </select>
          </div>
          
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-800 dark:text-white"
          >
            {statusOptions.map(status => (
              <option key={status} value={status}>
                {status === "All" ? "All Status" : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Bar */}
      {cards.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {currentCards.length} cards on Day {actualCurrentDay} of {filteredCards.length} total modules
              {searchQuery || selectedType !== "All" || selectedStatus !== "All" 
                ? ` (filtered from ${cards.length} total)` 
                : ""}
            </p>
            {cards.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const currentIndex = availableDays.indexOf(actualCurrentDay);
                    if (currentIndex > 0) {
                      handleDayChange(availableDays[currentIndex - 1]);
                    }
                  }}
                  disabled={actualCurrentDay === availableDays[0]}
                  className={`px-3 py-1 rounded text-sm ${
                    actualCurrentDay === availableDays[0]
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous Day
                </button>
                <span className="text-sm text-gray-600">
                  Day {actualCurrentDay} of {availableDays.length} days
                </span>
                <button
                  onClick={() => {
                    const currentIndex = availableDays.indexOf(actualCurrentDay);
                    if (currentIndex < availableDays.length - 1) {
                      handleDayChange(availableDays[currentIndex + 1]);
                    }
                  }}
                  disabled={actualCurrentDay === availableDays[availableDays.length - 1]}
                  className={`px-3 py-1 rounded text-sm ${
                    actualCurrentDay === availableDays[availableDays.length - 1]
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next Day
                </button>
              </div>
            )}
          </div>
          
          {/* Summary Stats */}
          <div className="mt-3 grid grid-cols-3 gap-4 rounded-lg bg-gray-50 dark:bg-navy-800 p-4">
            <div className="text-center">
              <p className="text-lg font-bold text-navy-700 dark:text-white">
                {cards.filter(card => card.type === "Quiz").length}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Quiz Modules</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-navy-700 dark:text-white">
                {cards.filter(card => card.type === "Tip").length}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Tip Modules</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-navy-700 dark:text-white">
                {cards.filter(card => card.type === "Link").length}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Link Modules</p>
            </div>
          </div>
        </div>
      )}

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
        {currentCards.map((card) => (
          <div
            key={card.id}
            className={`group rounded-lg border p-4 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col h-full ${
              card.status === "published"
                ? "border-gray-200 bg-white dark:border-gray-600 dark:bg-navy-800"
                : "border-gray-300 bg-gray-100 dark:border-gray-500 dark:bg-gray-700 opacity-75"
            }`}
          >
            {/* Card Header - Type/ID on left, Actions on right */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {getCardIcon(card.type)}
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {card.type}
                </span>
                <span className="text-xs font-mono text-brand-600 dark:text-brand-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                  {card.id}
                </span>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => {
                    const newStatus = card.status === "published" ? "draft" : "published";
                    onCardStatusToggle(card.id!, newStatus);
                  }}
                  className={`rounded p-1 transition-colors ${
                    card.status === "published"
                      ? "bg-green-100 hover:bg-green-200 dark:bg-green-900/20 dark:hover:bg-green-900/30"
                      : "bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30"
                  }`}
                  title={card.status === "published" ? "Published - Click to Unpublish" : "Draft - Click to Publish"}
                >
                  <MdVisibility className={`h-4 w-4 ${
                    card.status === "published"
                      ? "text-green-600 dark:text-green-400"
                      : "text-yellow-600 dark:text-yellow-400"
                  }`} />
                </button>
                <button
                  onClick={() => onCardDuplicate(card)}
                  className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Duplicate"
                >
                  <MdFileCopy className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <button
                  onClick={() => card.id && handleDeleteCard(card.id, card.title)}
                  className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/20"
                  title="Delete"
                >
                  <MdDelete className="h-4 w-4 text-red-500" />
                </button>
              </div>
            </div>

            {/* Card Header with Square Image */}
            <div className="flex items-start gap-3 mb-2">
              {card.assets && card.assets.length > 0 && card.assets[0].type === "image" && (
                <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 w-16 h-16 flex-shrink-0">
                  <img 
                    src={card.assets[0].url} 
                    alt={card.assets[0].name || card.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-navy-700 dark:text-white line-clamp-2 leading-tight">
                  {card.title || "Untitled Module"}
                </h4>
              </div>
            </div>

            {/* Card Content Preview - Flexible area that grows */}
            <div className="flex-1 flex flex-col">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                {card.content || "No content yet..."}
              </p>

              {/* Card Metadata */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadge(card.status)}`}>
                  {card.status}
                </span>
              </div>

              {/* Card Stats */}
              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                <span>{card.content.split(/\s+/).length} words</span>
                <span>{card.assets.length} assets</span>
              </div>

              {/* Updated Date */}
              {card.updatedAt && (
                <div className="mt-2 text-xs text-gray-400">
                  Updated {new Date(card.updatedAt).toLocaleDateString()}
                </div>
              )}
            </div>

            {/* Quick Actions - Fixed at bottom */}
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => handleEditCard(card)}
                className="flex-1 rounded bg-brand-500 px-3 py-2 text-xs font-medium text-white hover:bg-brand-600 transition duration-200"
              >
                <MdEdit className="inline h-3 w-3 mr-1" />
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State for Filtered Results */}
      {filteredCards.length === 0 && cards.length > 0 && (
        <div className="text-center py-8">
          <div className="mx-auto h-24 w-24 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4 overflow-hidden">
            <img 
              src="/assets/img/latte/50.png" 
              alt="No results found" 
              className="w-full h-full object-cover"
            />
          </div>
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
            No modules found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Try adjusting your search or filter criteria
          </p>
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedType("All");
              setSelectedStatus("All");
            }}
            className="rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Day Navigation Controls */}
      {cards.length > 0 && !showAllModules && (
        <div className="mt-6 mb-4 flex justify-center">
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleDayChange(availableDays[0])}
              disabled={actualCurrentDay === availableDays[0]}
              className={`px-3 py-2 rounded text-sm ${
                actualCurrentDay === availableDays[0]
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Day {availableDays[0]}
            </button>
            <button
              onClick={() => {
                const currentIndex = availableDays.indexOf(actualCurrentDay);
                if (currentIndex > 0) {
                  handleDayChange(availableDays[currentIndex - 1]);
                }
              }}
              disabled={actualCurrentDay === availableDays[0]}
              className={`px-3 py-2 rounded text-sm ${
                actualCurrentDay === availableDays[0]
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              ←
            </button>
            
            {/* Day numbers */}
            {availableDays.slice(0, Math.min(5, availableDays.length)).map((dayNum, i) => (
              <button
                key={dayNum}
                onClick={() => handleDayChange(dayNum)}
                className={`px-3 py-2 rounded text-sm ${
                  actualCurrentDay === dayNum
                    ? "bg-brand-500 text-white"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Day {dayNum}
              </button>
            ))}
            
            <button
              onClick={() => {
                const currentIndex = availableDays.indexOf(actualCurrentDay);
                if (currentIndex < availableDays.length - 1) {
                  handleDayChange(availableDays[currentIndex + 1]);
                }
              }}
              disabled={actualCurrentDay === availableDays[availableDays.length - 1]}
              className={`px-3 py-2 rounded text-sm ${
                actualCurrentDay === availableDays[availableDays.length - 1]
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              →
            </button>
            <button
              onClick={() => handleDayChange(availableDays[availableDays.length - 1])}
              disabled={actualCurrentDay === availableDays[availableDays.length - 1]}
              className={`px-3 py-2 rounded text-sm ${
                actualCurrentDay === availableDays[availableDays.length - 1]
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Day {availableDays[availableDays.length - 1]}
            </button>
            
            {/* Add/Remove Day Buttons */}
            <div className="ml-2 border-l border-gray-300 pl-2 flex gap-2">
              <button
                onClick={() => onAddDay(handleDayChange)}
                className="flex items-center gap-1 px-3 py-2 rounded text-sm bg-brand-500 text-white hover:bg-brand-600 transition-colors"
                title="Add a full day (12 cards) of learning content"
              >
                <MdAdd className="h-4 w-4" />
                Add Day
              </button>
              <button
                onClick={() => handleRemoveDay(actualCurrentDay)}
                disabled={totalDays <= 1}
                className={`flex items-center gap-1 px-3 py-2 rounded text-sm transition-colors ${
                  totalDays <= 1
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gray-500 text-white hover:bg-gray-600"
                }`}
                title={totalDays <= 1 ? "Cannot remove the last day" : `Remove all cards from Day ${actualCurrentDay}`}
              >
                <MdRemove className="h-4 w-4" />
                Remove This Day
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-2xl max-h-[90vh] mx-4 bg-white dark:bg-navy-800 rounded-lg shadow-xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
              <div>
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                  Edit Learning Module
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {editingCard.id}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <MdClose className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editingCard.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                  />
                </div>

                {/* Type and Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                      Type
                    </label>
                    <select
                      value={editingCard.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                    >
                      <option value="Quiz">Quiz</option>
                      <option value="Tip">Tip</option>
                      <option value="Link">Link</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                      Status
                    </label>
                    <select
                      value={editingCard.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                {/* Image Selection */}
                <div>
                  <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                    Card Image
                  </label>
                  <div className="flex items-center gap-3">
                    {editingCard.assets && editingCard.assets.length > 0 && editingCard.assets[0].type === "image" && (
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                        <img 
                          src={editingCard.assets[0].url}
                          alt={editingCard.assets[0].name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsImageSelectorOpen(true)}
                      className="px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 dark:bg-brand-900/20 dark:text-brand-300 dark:border-brand-700 dark:hover:bg-brand-900/30"
                    >
                      {editingCard.assets && editingCard.assets.length > 0 ? 'Change Image' : 'Select Image'}
                    </button>
                  </div>
                </div>

                {/* Content - Quiz specific or regular */}
                {editingCard.type === "Quiz" ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                        Question
                      </label>
                      <input
                        type="text"
                        value={quizData.question}
                        onChange={(e) => setQuizData(prev => ({ ...prev, question: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                        placeholder="Enter your question"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Option A:
                          </label>
                          <button
                            type="button"
                            onClick={() => setQuizData(prev => ({ 
                              ...prev, 
                              correctAnswers: { ...prev.correctAnswers, a: !prev.correctAnswers.a } 
                            }))}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                              quizData.correctAnswers.a 
                                ? 'bg-green-500 text-white shadow-md hover:bg-green-600' 
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                            }`}
                          >
                            {quizData.correctAnswers.a ? '✓ Correct Answer' : 'Mark as Correct'}
                          </button>
                        </div>
                        <input
                          type="text"
                          value={quizData.optionA}
                          onChange={(e) => setQuizData(prev => ({ ...prev, optionA: e.target.value }))}
                          className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                            quizData.correctAnswers.a
                              ? 'border-green-300 bg-white focus:ring-green-500 focus:border-green-500 dark:border-green-600 dark:bg-navy-900 dark:text-white'
                              : 'border-gray-300 bg-white focus:ring-brand-500 focus:border-brand-500 dark:border-gray-600 dark:bg-navy-900 dark:text-white'
                          }`}
                          placeholder="Enter first answer option"
                        />
                      </div>
                      
                      <div className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Option B:
                          </label>
                          <button
                            type="button"
                            onClick={() => setQuizData(prev => ({ 
                              ...prev, 
                              correctAnswers: { ...prev.correctAnswers, b: !prev.correctAnswers.b } 
                            }))}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                              quizData.correctAnswers.b 
                                ? 'bg-green-500 text-white shadow-md hover:bg-green-600' 
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                            }`}
                          >
                            {quizData.correctAnswers.b ? '✓ Correct Answer' : 'Mark as Correct'}
                          </button>
                        </div>
                        <input
                          type="text"
                          value={quizData.optionB}
                          onChange={(e) => setQuizData(prev => ({ ...prev, optionB: e.target.value }))}
                          className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                            quizData.correctAnswers.b
                              ? 'border-green-300 bg-white focus:ring-green-500 focus:border-green-500 dark:border-green-600 dark:bg-navy-900 dark:text-white'
                              : 'border-gray-300 bg-white focus:ring-brand-500 focus:border-brand-500 dark:border-gray-600 dark:bg-navy-900 dark:text-white'
                          }`}
                          placeholder="Enter second answer option"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-700">
                      <div className="text-blue-600 dark:text-blue-400">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="text-xs text-blue-800 dark:text-blue-300">
                        <strong>Tip:</strong> Click "Mark as Correct" buttons to designate the right answers. Multiple answers can be correct.
                      </div>
                    </div>
                  </div>
                ) : editingCard.type === "Link" ? (
                  <div>
                    <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                      Resource Link
                    </label>
                    <input
                      type="text"
                      value={editingCard.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                      placeholder="[Link Title](https://example.com)"
                    />
                    <div className="mt-1 text-xs text-gray-500">
                      Format: [Link Title](URL) - {editingCard.content.length} characters
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-navy-700 dark:text-white mb-2">
                      Content
                    </label>
                    <textarea
                      value={editingCard.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      rows={12}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-gray-600 dark:bg-navy-900 dark:text-white resize-none"
                      placeholder="Enter your content here. You can use Markdown formatting."
                    />
                    <div className="mt-1 text-xs text-gray-500">
                      {editingCard.content.length} characters
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-navy-900">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-500 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCard}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600"
              >
                <MdSave className="h-4 w-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Selector Modal */}
      {isImageSelectorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-4xl max-h-[90vh] mx-4 bg-white dark:bg-navy-800 rounded-lg shadow-xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Select Image from Library
              </h3>
              <button
                onClick={() => setIsImageSelectorOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <MdClose className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content - Image Grid */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                {availableImages.map((image) => (
                  <button
                    key={image.id}
                    onClick={() => handleImageSelect(image.url, image.name)}
                    className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 hover:ring-2 hover:ring-brand-500 transition-all duration-200 transform hover:scale-105"
                    title={image.name}
                  >
                    <img 
                      src={image.url}
                      alt={image.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmPopup
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText="Confirm"
        cancelText="Cancel"
        variant="danger"
      />
    </Card>
  );
};

export default CardManager;